import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, mergeMap, of, switchMap, takeUntil, tap } from 'rxjs';
import * as fromGenerated from '../../../_generated';
import * as fromHelper from '../../../analytics/analytics.helper';
import { AnalyticsService } from '../../../analytics/analytics.service';
import { AssistantFavoritesActions } from '../../../favorites/favorites.actions';
import * as fromFeatureFlags from '../../../feature-flags';
import { sentryCaptureError } from '../../../sentry.helper';
import { convertLangToAssistantLocale } from '../assistants.helper';
import {
  defaultChatLockKey,
  readApiErrorName,
} from '../default-chat-lock.helper';
import {
  AssistantActions,
  AssistantBuilderModalActions,
  AssistantsActions,
} from './assistants.actions';

@Injectable()
export class AssistantsEffects {
  private readonly actions$ = inject(Actions);
  private readonly assistantsService = inject(fromGenerated.AssistantsService);
  private readonly adminService = inject(fromGenerated.AdminService);
  private readonly translateService = inject(TranslateService);
  private readonly toastrService = inject(ToastrService);
  private readonly analyticsService = inject(AnalyticsService);

  loadAssistants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantsActions.loadAssistants),
      switchMap(() =>
        this.assistantsService
          .assistantsControllerGetAssistantsV1(
            undefined,
            convertLangToAssistantLocale(
              this.translateService.getCurrentLang(),
            ),
          )
          .pipe(
            map((assistants) =>
              AssistantsActions.loadAssistantsSuccess({
                assistants,
                loadedAt: Date.now(),
              }),
            ),
            catchError((error: Error) =>
              of(
                AssistantsActions.loadAssistantsFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  onLoadFeatureFlags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromFeatureFlags.FeatureFlagsActions.loadFeatureFlags),
      map(() => AssistantsActions.loadAssistants()),
    ),
  );

  /**
   * The admin GET is the only read that returns the prompt. The view stays
   * on the modal snapshot: writing it into the entity map would
   * put an unredacted prompt where `GET /assistants` refuses to. A pending
   * read is dropped when the builder opens another skill, opens Create or
   * closes, so a late response cannot replace the newer modal state.
   */
  openManagedEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantBuilderModalActions.openManagedEdit),
      switchMap(({ uuid }) =>
        this.adminService.adminManagedSkillsControllerGetV1(uuid).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(
                AssistantBuilderModalActions.openEdit,
                AssistantBuilderModalActions.openCreate,
                AssistantBuilderModalActions.close,
              ),
            ),
          ),
          map((assistant) =>
            AssistantBuilderModalActions.openManagedEditSuccess({ assistant }),
          ),
          sentryCaptureError,
          catchError(() =>
            of(AssistantBuilderModalActions.openManagedEditFailure()),
          ),
        ),
      ),
    ),
  );

  openManagedEditFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssistantBuilderModalActions.openManagedEditFailure),
        tap(() => {
          this.toastrService.error(
            this.translateService.instant(
              'ASSISTANTS.ASSISTANT_BUILDER.OPEN_ERROR',
            ) as string,
            undefined,
            { closeButton: true, disableTimeOut: true },
          );
        }),
      ),
    { dispatch: false },
  );

  // mergeMap, not switchMap: a second delete confirmed before the first
  // answers must not cancel it, or the first skill's result and its toast
  // never arrive.
  deleteAssistant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.deleteAssistant),
      mergeMap(({ uuid, assistant }) =>
        this.assistantsService.assistantsControllerDeleteAssistantV1(uuid).pipe(
          map(() =>
            AssistantActions.deleteAssistantSuccess({ uuid, assistant }),
          ),
          sentryCaptureError,
          catchError((error: Error) =>
            of(
              AssistantActions.deleteAssistantFailure({
                error: error.message,
                errorName: readApiErrorName(error) ?? null,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // The one success toast for every skill delete: the owner flow above
  // (My Skills and the builder), the builder's admin delete and the admin
  // page's Supprimer, which both dispatch this success. The words are the
  // admin page's own keys, which fit every path.
  deleteAssistantSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssistantActions.deleteAssistantSuccess),
        tap(() =>
          this.toastrService.success(
            this.translateService.instant(
              'ADMIN.MANAGED_SKILLS.TOAST.DELETE_SUCCESS',
            ) as string,
            undefined,
            { closeButton: true },
          ),
        ),
      ),
    { dispatch: false },
  );

  // Only the owner flow dispatches this failure; the builder closes before
  // the answer, so without a toast a failed delete went unnoticed. A refusal
  // the default chat lock names (SB-3944) gets its own words.
  deleteAssistantFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssistantActions.deleteAssistantFailure),
        tap(({ errorName }) =>
          this.toastrService.error(
            this.translateService.instant(
              defaultChatLockKey(errorName) ??
                'ADMIN.MANAGED_SKILLS.TOAST.DELETE_ERROR',
            ) as string,
            undefined,
            { closeButton: true, disableTimeOut: true },
          ),
        ),
      ),
    { dispatch: false },
  );

  skillDeletedAnalytics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssistantActions.deleteAssistantSuccess),
        mergeMap(({ assistant }) =>
          this.analyticsService.sendSkillDeletedEvent(
            fromHelper.toSkillLifecycleEvent(assistant),
          ),
        ),
      ),
    { dispatch: false },
  );

  skillFavoritedAnalytics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssistantFavoritesActions.addFavoriteSuccess),
        mergeMap(({ assistant }) =>
          this.analyticsService.sendSkillFavoritedEvent(
            fromHelper.toSkillLifecycleEvent(assistant),
          ),
        ),
      ),
    { dispatch: false },
  );

  skillUnfavoritedAnalytics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AssistantFavoritesActions.removeFavoriteSuccess),
        mergeMap(({ assistant }) =>
          this.analyticsService.sendSkillUnfavoritedEvent(
            fromHelper.toSkillLifecycleEvent(assistant),
          ),
        ),
      ),
    { dispatch: false },
  );
}
